import { CalendarIcon, X } from 'lucide-react';
import { ComponentProps, ReactNode } from 'react';
import { SelectSingleEventHandler } from 'react-day-picker';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';

import { Button } from '@/components/ui/button.js';
import { Calendar } from '@/components/ui/calendar.js';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.js';
import { cn } from '@/lib/tailwind-utils.js';
import { localDateFromPlainDate, plainDateFromLocalDate } from '@/lib/time-zones.js';

interface FormInputDateProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label: string;
  triggerButtonProps?: ComponentProps<typeof Button> & { 'data-testid'?: string };
  required?: boolean;
  showClearDateButton?: boolean;
  description?: ReactNode;
  className?: string;
  /** Earliest selectable calendar day (inclusive). Earlier days are disabled. */
  minDate?: Temporal.PlainDate;
}

export function FormInputDate<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  form,
  name,
  label,
  triggerButtonProps = {},
  required = false,
  showClearDateButton = false,
  description,
  className,
  minDate,
}: FormInputDateProps<TFieldValues, TName>) {
  const { className: triggerButtonClassName, ...otherTriggerProps } = triggerButtonProps;
  const minDateAsJsDate = minDate ? localDateFromPlainDate(minDate) : undefined;
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const fieldValue = field.value as Temporal.PlainDate | undefined;
        const selected = fieldValue ? localDateFromPlainDate(fieldValue) : undefined;
        const onSelect: SelectSingleEventHandler = (_, selectedDay) => {
          field.onChange(plainDateFromLocalDate(selectedDay));
        };
        return (
          <FormItem className={cn('flex flex-col', className)}>
            <FormLabel className="font-bold">
              {label}
              {required && '*'}
            </FormLabel>
            <Popover>
              <div className="flex">
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant={'outline'}
                      className={cn(
                        'w-[240px] pl-3 text-left font-normal',
                        !field.value && 'text-muted-foreground',
                        triggerButtonClassName,
                      )}
                      {...otherTriggerProps}
                    >
                      {fieldValue ? (
                        fieldValue.toLocaleString('en-US', { dateStyle: 'long' })
                      ) : (
                        <span>Pick a date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                {showClearDateButton && (
                  <Button
                    type="button"
                    onClick={() => field.onChange(undefined)}
                    variant="ghost"
                    size="icon"
                    title="Clear date"
                    className="text-muted-foreground"
                  >
                    <X size={16} />
                  </Button>
                )}
              </div>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selected}
                  onSelect={onSelect}
                  disabled={minDateAsJsDate ? { before: minDateAsJsDate } : undefined}
                  fromDate={minDateAsJsDate}
                  defaultMonth={selected ?? minDateAsJsDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
